import { useEffect, useRef, useState } from "react";
import type { Candle } from "@bubblefun/shared";
import type { IChartApi, ISeriesApi, LineData, Time, UTCTimestamp } from "lightweight-charts";

const VISIBLE_CANDLES = 36;

function lineData(candles: Candle[]): LineData<Time>[] {
  const values = new Map<number, number>();

  for (const candle of candles.slice(-VISIBLE_CANDLES * 2)) {
    if (!Number.isFinite(candle.time) || !Number.isFinite(candle.close)) continue;
    const timestamp = Math.floor(candle.time > 10_000_000_000 ? candle.time / 1_000 : candle.time);
    values.set(timestamp, candle.close);
  }

  const points = [...values]
    .sort(([left], [right]) => left - right)
    .slice(-VISIBLE_CANDLES)
    .map(([time, value]) => ({ time: time as UTCTimestamp, value }));

  if (points.length === 1) {
    return [{ time: (Number(points[0].time) - 300) as UTCTimestamp, value: points[0].value }, points[0]];
  }

  return points;
}

export function MiniPriceChart({ candles, positive, label }: { candles: Candle[]; positive: boolean; label: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const candleRef = useRef(candles);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    candleRef.current = candles;
    const series = seriesRef.current;
    if (!series) return;
    series.setData(lineData(candles));
    chartRef.current?.timeScale().fitContent();
  }, [candles]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setVisible(true);
      observer.disconnect();
    }, { rootMargin: "120px" });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!visible || !container) return;
    let disposed = false;
    let chart: IChartApi | null = null;

    void import("lightweight-charts").then(({ ColorType, CrosshairMode, LineSeries, createChart }) => {
      if (disposed) return;

      chart = createChart(container, {
        autoSize: true,
        height: container.clientHeight || 43,
        layout: {
          background: { type: ColorType.Solid, color: "transparent" },
          textColor: "transparent",
          attributionLogo: false
        },
        grid: {
          vertLines: { visible: false },
          horzLines: { visible: false }
        },
        leftPriceScale: { visible: false, borderVisible: false },
        rightPriceScale: { visible: false, borderVisible: false },
        timeScale: {
          visible: false,
          borderVisible: false,
          fixLeftEdge: true,
          fixRightEdge: true,
          timeVisible: false,
          secondsVisible: false
        },
        crosshair: { mode: CrosshairMode.Hidden },
        handleScroll: false,
        handleScale: false,
        kineticScroll: { mouse: false, touch: false }
      });

      const series = chart.addSeries(LineSeries, {
        color: positive ? "#62e879" : "#ff634d",
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false
      });
      series.priceScale().applyOptions({ autoScale: true, scaleMargins: { top: 0.14, bottom: 0.14 } });
      series.setData(lineData(candleRef.current));
      chart.timeScale().fitContent();
      chartRef.current = chart;
      seriesRef.current = series;
    });

    return () => {
      disposed = true;
      seriesRef.current = null;
      chartRef.current = null;
      chart?.remove();
    };
  }, [positive, visible]);

  return <div ref={containerRef} className="mini-price-chart" role="img" aria-label={label} />;
}
